// Utilidad para dibujar la curva caudal/presión de una bomba sobre un canvas
// nativo (sin DOM) y obtener un dataURL listo para insertar en el PDF.

import {
  convertCaudal,
  convertPresion,
  type UnidadCaudal,
  type UnidadPresion,
} from './units';

export interface PuntoCurva {
  pct: number;
  caudal: number;
  presion: number;
}

export interface CurvaCaudalData {
  nominal: { caudal: number; presion: number };
  puntos: PuntoCurva[];
}

export interface RenderCurvaOptions extends CurvaCaudalData {
  ancho?: number;
  alto?: number;
  titulo?: string;
  /** Etiquetas legibles para los ejes (p.ej. 'l/min', 'bar', 'psi'…). */
  unidadCaudalLabel?: string;
  unidadPresionLabel?: string;
  /** Unidad en la que están expresados los valores de placa (nominal). */
  unidadCaudalPlaca?: UnidadCaudal;
  unidadPresionPlaca?: UnidadPresion;
  /** Unidad de los instrumentos (caudalímetro / manómetro) en sala. */
  unidadCaudalInstrumento?: UnidadCaudal;
  unidadPresionInstrumento?: UnidadPresion;
}

/**
 * Dibuja la curva sobre un canvas en memoria y devuelve un dataURL PNG.
 * Pensada para usarse tanto en el preview de la UI como en la generación
 * del PDF (no depende del DOM visible).
 */
export function renderCurvaCaudalToDataURL(opts: RenderCurvaOptions): string {
  const W = opts.ancho ?? 900;
  const H = opts.alto ?? 480;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Fondo blanco
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  const padL = 70;
  const padR = 30;
  const padT = opts.titulo ? 40 : 20;
  const padB = 60;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  // Título
  if (opts.titulo) {
    ctx.fillStyle = '#111';
    ctx.font = 'bold 18px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(opts.titulo, W / 2, 24);
  }

  const puntos = [...opts.puntos].sort((a, b) => a.caudal - b.caudal);
  const uCaudal = opts.unidadCaudalLabel || 'l/min';
  const uPresion = opts.unidadPresionLabel || 'bar';

  // El nominal se almacena en unidades de placa, pero los puntos y los ejes
  // se expresan en unidades de los instrumentos (caudalímetro/manómetro).
  // Convertimos el nominal a la unidad del instrumento para que las escalas
  // de los ejes y la línea de referencia coincidan con la realidad medida.
  const uPlacaC = opts.unidadCaudalPlaca;
  const uInstrC = opts.unidadCaudalInstrumento;
  const uPlacaP = opts.unidadPresionPlaca;
  const uInstrP = opts.unidadPresionInstrumento;
  const nominalCaudalInstr = uPlacaC && uInstrC
    ? convertCaudal(opts.nominal.caudal, uPlacaC, uInstrC)
    : opts.nominal.caudal;
  const nominalPresionInstr = uPlacaP && uInstrP
    ? convertPresion(opts.nominal.presion, uPlacaP, uInstrP)
    : opts.nominal.presion;

  const maxCaudal = Math.max(
    nominalCaudalInstr * 1.6,
    ...puntos.map(p => p.caudal),
    1,
  );
  const maxPresion = Math.max(
    nominalPresionInstr * 1.5,
    ...puntos.map(p => p.presion),
    1,
  );

  const xFor = (c: number) => padL + (c / maxCaudal) * plotW;
  const yFor = (p: number) => padT + plotH - (p / maxPresion) * plotH;

  // Rejilla
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 1;
  ctx.font = '11px Helvetica, Arial, sans-serif';
  ctx.fillStyle = '#6b7280';
  ctx.textAlign = 'right';
  const yTicks = 5;
  for (let i = 0; i <= yTicks; i++) {
    const v = (maxPresion / yTicks) * i;
    const y = yFor(v);
    ctx.beginPath();
    ctx.moveTo(padL, y);
    ctx.lineTo(W - padR, y);
    ctx.stroke();
    ctx.fillText(v.toFixed(1), padL - 6, y + 4);
  }
  ctx.textAlign = 'center';
  const xTicks = 6;
  for (let i = 0; i <= xTicks; i++) {
    const v = (maxCaudal / xTicks) * i;
    const x = xFor(v);
    ctx.beginPath();
    ctx.moveTo(x, padT);
    ctx.lineTo(x, padT + plotH);
    ctx.stroke();
    ctx.fillText(Math.round(v).toString(), x, padT + plotH + 16);
  }

  // Ejes
  ctx.strokeStyle = '#111';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(padL, padT);
  ctx.lineTo(padL, padT + plotH);
  ctx.lineTo(W - padR, padT + plotH);
  ctx.stroke();

  // Línea de referencia: presión nominal (en la unidad del manómetro)
  if (nominalPresionInstr > 0) {
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = '#9ca3af';
    ctx.lineWidth = 1;
    const yN = yFor(nominalPresionInstr);
    ctx.beginPath();
    ctx.moveTo(padL, yN);
    ctx.lineTo(W - padR, yN);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#6b7280';
    ctx.textAlign = 'left';
    const refTxt = nominalPresionInstr >= 10
      ? nominalPresionInstr.toFixed(1)
      : nominalPresionInstr.toFixed(2);
    ctx.fillText(`Presión nominal ${refTxt} ${uPresion}`, padL + 6, yN - 4);
  }

  // Curva
  if (puntos.length >= 2) {
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    puntos.forEach((p, i) => {
      const x = xFor(p.caudal);
      const y = yFor(p.presion);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }

  // Puntos
  ctx.fillStyle = '#1d4ed8';
  puntos.forEach(p => {
    const x = xFor(p.caudal);
    const y = yFor(p.presion);
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
  });

  // Etiquetas de % junto a cada punto
  ctx.fillStyle = '#111';
  ctx.font = 'bold 11px Helvetica, Arial, sans-serif';
  ctx.textAlign = 'left';
  puntos.forEach(p => {
    const x = xFor(p.caudal);
    const y = yFor(p.presion);
    ctx.fillText(`${p.pct}%`, x + 7, y - 6);
  });

  // Etiquetas ejes
  ctx.fillStyle = '#374151';
  ctx.font = '12px Helvetica, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`Caudal (${uCaudal})`, padL + plotW / 2, H - 18);
  ctx.save();
  ctx.translate(18, padT + plotH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(`Presión (${uPresion})`, 0, 0);
  ctx.restore();

  return canvas.toDataURL('image/png');
}

export const PUNTOS_DEFAULT: number[] = [0, 25, 50, 75, 100, 125, 140];

export function curvaVacia(nominalCaudal = 0, nominalPresion = 0): CurvaCaudalData & { resultado: null; observaciones: string } {
  return {
    nominal: { caudal: nominalCaudal, presion: nominalPresion },
    puntos: PUNTOS_DEFAULT.map(pct => ({
      pct,
      caudal: nominalCaudal ? Math.round((nominalCaudal * pct) / 100) : 0,
      presion: 0,
    })),
    resultado: null,
    observaciones: '',
  };
}

